import React, { useState } from 'react';
import { Progress, Typography } from 'antd';
import Button from "@common/Button";
import PopConfirm from "@common/PopConfirm";
import { CheckRounded, ChevronRightRounded, CloseRounded, DeleteRounded, DragIndicatorRounded, EditRounded } from "@mui/icons-material";
import { STATUS_CONFIG } from './LessonPlannerStatusButton';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Input } from 'antd';

const STATUS_ORDER = ['PLANNED', 'ACTIVE', 'COMPLETE'];

const LessonPlannerCardButton = ({ id, title, subtitle, selected = false, onClick, onArchive, onRename, lessons, classStatuses, selectMode = false, checked = false, onToggle }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(title);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
    };

    const startEdit = (e) => {
        e.stopPropagation();
        setEditValue(title);
        setIsEditing(true);
    };

    const commitEdit = () => {
        setIsEditing(false);
        if (editValue.trim() && editValue.trim() !== title) {
            onRename?.(editValue.trim());
        }
    };

    const cancelEdit = () => {
        setIsEditing(false);
        setEditValue(title);
    };

    if (isEditing) {
        return (
            <div ref={setNodeRef} style={style} className="ch-lesson-planner-card-edit-state">
                <Input
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onPressEnter={commitEdit}
                    onKeyDown={e => e.key === 'Escape' && cancelEdit()}
                    autoFocus
                />
                <div className="ch-lesson-planner-card-edit-actions">
                    {onArchive && (
                        <PopConfirm
                            title="Delete this item?"
                            okText="Delete"
                            cancelText="Cancel"
onConfirm={() => { setIsEditing(false); onArchive(); }}
                            placement="top"
                        >
                            <Button type="text" aria-label="Delete">
                                <DeleteRounded style={{ fontSize: '18px' }} />
                            </Button>
                        </PopConfirm>
                    )}
                    <div className="ch-lesson-planner-card-edit-actions-right">
                        <Button type="outline" aria-label="Cancel" onClick={cancelEdit}>
                            <CloseRounded style={{ fontSize: '18px' }} />
                        </Button>
                        <Button type="primary" aria-label="Save" onClick={commitEdit}>
                            <CheckRounded style={{ fontSize: '18px' }} />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    let progressBar = null;
    if (lessons) {
        const total = lessons.length;
        const complete = lessons.filter(l =>
            (l.classStatuses || []).length > 0 &&
            (l.classStatuses || []).every(cs => cs.lessonState === 'COMPLETE')
        ).length;
        const pct = total > 0 ? Math.round((complete / total) * 100) : 0;
        progressBar = (
            <div
                className="ch-lesson-planner-card-progress"
                role="meter"
                aria-valuenow={complete}
                aria-valuemin={0}
                aria-valuemax={total}
                aria-label={`${complete} of ${total} lessons complete`}
                onClick={e => e.stopPropagation()}
            >
                <Progress
                    percent={pct}
                    showInfo={false}
                    strokeColor={pct === 100 ? '#52c41a' : '#1677ff'}
                    railColor="rgba(0,0,0,0.12)"
                    size="small"
                />
                <div className="ch-lesson-planner-card-progress-labels">
                    <span>{complete} / {total} done</span>
                    <span>{pct}%</span>
                </div>
            </div>
        );
    }

    const lowestStatus = classStatuses?.length
        ? STATUS_ORDER.find(s => classStatuses.some(cs => cs.lessonState === s))
        : null;
    const dotColor = lowestStatus ? STATUS_CONFIG[lowestStatus].color : '#ccc';

    const cardClass = [
        'ch-lesson-planner-card-button',
        selected && !selectMode ? 'selected' : '',
        selectMode && checked ? 'ch-lesson-planner-card-button--checked' : '',
    ].filter(Boolean).join(' ');

    return (
        <div ref={setNodeRef} style={style} className={`ch-lesson-planner-card-button-wrapper${lessons ? ' ch-lesson-planner-card-button-wrapper--with-progress' : ''}`}>
            <button
                className={cardClass}
                onClick={selectMode ? (e) => onToggle(e) : onClick}
                role={selectMode ? 'checkbox' : undefined}
                aria-checked={selectMode ? checked : undefined}
            >
                <div className="ch-lesson-planner-card-button-top">
                    {selectMode ? (
                        <span className="ch-lesson-planner-card-checkbox" aria-hidden="true">
                            {checked && <CheckRounded style={{ fontSize: '14px' }} />}
                        </span>
                    ) : (
                        <span
                            className="ch-lesson-planner-card-drag-handle"
                            {...attributes}
                            {...listeners}
                            onClick={e => e.stopPropagation()}
                        >
                            <DragIndicatorRounded style={{ fontSize: '18px' }} />
                        </span>
                    )}
                    {classStatuses !== undefined && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0, alignSelf: 'center' }} />}
                    <div className="ch-lesson-planner-card-button-text">
                        <Typography.Text className="ch-lesson-planner-card-button-title" ellipsis={{ tooltip: title }}>{title}</Typography.Text>
                        {subtitle && <span className="ch-lesson-planner-card-button-subtitle">{subtitle}</span>}
                    </div>
                    <div className="ch-lesson-planner-card-button-spacer" />
                    {!selectMode && <ChevronRightRounded style={{ color: selected ? 'var(--secondary-colour)' : undefined }} />}
                </div>
                {progressBar}
            </button>
            {!selectMode && (onArchive || onRename) && (
                <Button type="text" className="ch-lesson-planner-card-edit-btn" onClick={startEdit} aria-label={`Edit ${title}`}>
                    <EditRounded style={{ fontSize: '18px' }} />
                </Button>
            )}
        </div>
    );
};

export default LessonPlannerCardButton;
